import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

import { PATHS } from "@app/core/constants/path.const";
import { ENotificationEntityType, TNotification, TNotificationStrategy, } from "@app/core/notifications/types/notification.type";
import { TimeAgoPipe } from "@app/shared/pipes/time-ago.pipe";
import { TranslatePipe } from "@ngx-translate/core";

const NOTIFICATION_STRATEGY_MAP = new Map<ENotificationEntityType, (notification: TNotification) => TNotificationStrategy>(
  [
    [
      ENotificationEntityType.PolicyVersion, (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.policies.path}/${PATHS.policies.versionDetails.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_POLICY",
      })
    ],
    [
      ENotificationEntityType.Client, (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.clients.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_CLIENT",
      })
    ],
    [
      ENotificationEntityType.Invoice, (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.billing.path}/${PATHS.billing.invoices.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_INVOICE",
      })
    ],
    [
      ENotificationEntityType.Claim, (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.claims.path}/${PATHS.claims.directory.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_CLAIM",
      })
    ],
    [
      ENotificationEntityType.Policy, (notification: TNotification) => ({
      ...notification,
      buildURL: null,
      action: "NONE",
      actionLabel: null,
      })
    ]
]);

function toInternalPath(linkUrl: string | null): string | null {
  if (!linkUrl) {
    return null;
  }

  try {
    return new URL(linkUrl, window.location.origin).pathname;
  } catch {
    return null;
  }
}

function transformNotification(notification: TNotification): TNotificationStrategy {
  const notificationStrategy = NOTIFICATION_STRATEGY_MAP.get(notification.entityType);

  if (notification.entityId && notificationStrategy) {
    return notificationStrategy(notification);
  }

  const linkPath = toInternalPath(notification.linkUrl);

  if (linkPath) {
    return { ...notification, buildURL: linkPath, action: "REDIRECT", actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_DETAILS" };
  }

  return { ...notification, buildURL: null, action: "NONE", actionLabel: null };
}

@Component({
  selector: "under-ui-notification-item",
  templateUrl: "./notification-item.component.html",
  styleUrl: "./notification-item.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TimeAgoPipe,
    TranslatePipe,
  ],
  standalone: true,
})
export class NotificationItemComponent {
  @Input({ required: true, transform: transformNotification }) notification!: TNotificationStrategy;

  @Output() executeAction = new EventEmitter<TNotificationStrategy>();
  @Output() markAsRead = new EventEmitter<TNotificationStrategy>();
}
